import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createSelector } from 'reselect';
import {
  joyDocHelper,
  tableHelper,
  generateObjectId
} from '@builttocreate/engine-utils';

import { applyLogic } from '@joyfill/conditional-logic';

import Page from './Page';
import PageList from './PageList';
import LoadingIndicator from '../misc/LoadingIndicator';
import BuilderTabs from './fields/tree/Tabs';

import TableEditorSettings from './settings/TableEditorSettings';
import FieldSettings, { TableColumnSettings } from './settings/FieldSettings';
import PageSettings from './settings/PageSettings';
import FileSettings from './settings/FileSettings';
import CollectionEditorSettings from './settings/CollectionEditorSettings';

import { ensureTableColumnsHaveRequiredProperties, ensureCollectionSchemaTableColumnsHaveRequiredProperties, generateId, calculatePagePercentageAdjustments, getPercentageAdjustedValue } from '../../lib/documentHelper';
import { parseNavigationPath } from '../../lib/navigator';


import FieldTypes from '../../constants/FieldTypes';
import FieldDragSourceTypes from '../../constants/FieldDragSourceTypes';
import FieldModes from '../../constants/FieldModes';
import FileViews from '../../constants/FileViews';
import FieldDisplayTypes from '../../constants/FieldDisplayTypes';
import FieldPresentationModes from '../../constants/FieldPresentationModes';

import ThemeContext from '../../theme/ThemeContext';

const { getColumnOrder, getRowOrder } = tableHelper;
const { sortPages } = joyDocHelper;

const StyledWrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: row;
	flex: 1;
	font-family: ${(props) => props.theme.fontFamily};

	* {
		font-family: ${(props) => props.theme.fontFamily} !important;
	}
`;

const StyledLoadingWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
`;

/**
 * >>>>>>>>> Start Left Panel Styles <<<<<<<<<<<
 */
const StyledLeftPanelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 220px;
	min-width: 215px;
	margin-right: 8px;
	background-color: ${(props) => props.theme.backgroundColor};
	${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
	border-radius: ${(props) => props.theme.borderRadius}px;
`;
/**
 * >>>>>>>>> End Left Panel Styles <<<<<<<<<<<
 */

/**
 * >>>>>>>>> Start Right Panel Styles <<<<<<<<<<<
 */
const StyledRightPanelWrapper = styled.div`
	width: 220px;
	min-width: 200px;
	margin-left: 8px;
	overflow-y: auto;
	overflow-x: hidden;
	background-color: ${(props) => props.theme.backgroundColor};
	${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
	border-radius: ${(props) => props.theme.borderRadius}px;
`;

/**
 * >>>>>>>>> End Right Panel Styles <<<<<<<<<<<
 */

/**
 * >>>>>>>>> Start Middle Panel Styles <<<<<<<<<<<
 */
const StyledMiddlePanel = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	margin-right: auto;
	margin-left: auto;
`;

const StyledMiddlePanelBody = styled.div`
	display: flex;
	flex: 1;
	justify-content: center;
  max-height: ${props => `${props.$maxHeight}px` || 'auto'};
	background-color: ${(props) => props.theme.backgroundColor};
	${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
	border-radius: ${(props) => props.theme.borderRadius}px;
	overflow-y: auto;
	overflow-x: hidden;
`;

const StyledMiddlePanelTableSettings = styled.div`
	margin-top: 4px;
`;

const StyledMiddlePanelPageBarWrapper = styled.div`
	max-width: 100%;
	background-color: ${(props) => props.theme.backgroundColor};
	display: flex;
	flex-direction: row;
	${(props) => (props.$isMobileView ? 'margin-bottom: 4px;' : 'margin-top: 4px;')};
	${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
	border-radius: ${(props) => props.theme.borderRadius}px;
`;

const StyledMiddlePanelLoader = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
`;
/**
 * >>>>>>>>> End Middle Panel Styles <<<<<<<<<<<
 */

/**
 * >>>>>>>>> Start Middle Panel Styles <<<<<<<<<<<
 */
const StyledHeaderFooterWrapper = styled.div`
	display: flex;
  flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
  background-color: blue;
  height: 100px;
  max-height: 100px;
`;
/**
 * >>>>>>>>> End Middle Panel Styles <<<<<<<<<<<
 */

const File = ({
  mode = FieldModes.fill,

  /**
   * Document properties
   */
  fields, //Raw fields returned on doc

  /**
   * File Properties
   */
  id,
  name,

  width,
  height,

  styles = {},
  header,
  footer,
  pages,
  pageOrder,

  /**
   * JWT license key for validation
   */
  licenseValidation,

  /**
   * Overwrite and default select a page and field via prop
   */
  initialPageId, //Overwrite internal selected page

  /**
   * What optional features should be enabled or disabled.
   * * formulas: boolean
   * * formulasSandbox: boolean
   * * readableIds: boolean
   * * repeatingHeaderAndFooter: boolean
   */
  features,

  /**
   * Array of field options that can be added to the
   * form. These display inside the field list on
   * the left side of the form builder.
   */
  fieldOptions,

  /**
   * Field settings config object
   */
  fieldSettings,

  /**
   * Determine if field lazy loading is enabled or disabled
   */
  enableLazyLoadFields,

  /**
   * File action handler
   */
  onFileChange,
  onFileUpload,
  onCapture,
  onDocumentChange,

  /**
   * Page change(s) action handler
   */
  onPageCreate,
  onPageChange,
  onPageUpload,
  onPageUploadDelete,
  onPageDelete,
  onPageDuplicate,
  onPageMoveLeft,
  onPageMoveRight,

  /**
   * Field change(s) action handler
   */
  onFieldFileUpload,
  onFieldFileDelete,
  onFieldFileClick,

  onFieldCreate,
  onFieldDelete,
  onFieldChange,
  onFieldDuplicate,
  onFieldPositionDuplicate,
  onFieldPositionChange,
  onFieldRowAddBelow,
  onFieldRowDuplicate,
  onFieldRowMoveUp,
  onFieldRowMoveDown,
  onFieldRowCellChange,
  onFieldRowCellUpload,
  onFieldRowDelete,
  onFieldColumnDelete,


  onFocus,
  onSelect,
  onBlur,
  onPageFocus,
  onPageBlur,

  /**
   * Meausurement Properties:
   * measure: Boolean - Whether to enable field measurement rendering for fields that support it.
   * updateTimestamps: Object - A map of section fields ids to and timestamps of when the field was last updated.
   * measurements: Object - A map of section field ids and their measurements.
   * onMeasurementsCompleted: Function - A function that is called when all fields have been measured for a given section.
   */
  measure,
  updateTimestamps,
  measurements,
  onMeasurementsCompleted,

  /**
   * onFieldLayoutsChange is fired with entire array of field positions
   * anytime any layout values change.
   */
  onFieldLayoutsChange,

  /**
   * View Helpers
   */
  view,
  views,
  onViewChange,
  onToggleViewSync,
  onUploadAsync,

  doc,

  /**
   * Mock monitor for testing. I couldn't figure out any
   * reasonable way to mock the monitor in order to
   * get a client offset value that is valid and testable.
   * Without the mock the client offset always returns
   * x and y values as undefined.
   */
  mockTestDndMonitor, //Only for testing

  documentId,
  documentIdentifier,

  /**
   * Navigator instance for programmatic navigation
   */
  navigator,

}) => {

  //Temporary until we have a better way to handle this
  const useReadableIds = features?.readableIds;

  const theme = useContext(ThemeContext);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START VIEW HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * Mobile view detects if the screen should be optimized
   * for mobile forms experience. The mobile view experience is
   * optimized for fill and read-only modes.
   */
  const isMobileView = view === FileViews.mobile && mode !== FieldModes.edit;

  const hasMobileView = !views
    ? false
    : views.find((view) => view.type === FileViews.mobile);
  const modeOverride =
    view === FileViews.mobile && !hasMobileView && mode !== FieldModes.fill
      ? FieldModes.readonly
      : mode;

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END VIEW HELPERS <<<<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START PAGE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  const [pageLoading, setPageLoading] = useState(false);
  const [activePageSettingsPageId, setActivePageSettingsPageId] = useState(null);

  const sortedPages = applyLogicAndSortPagesSelector({
    pages,
    pageOrder,
    fields,
    mode,
  });

  const [pageId, setPageId] = useState(
    getActivePageIdSelector({ sortedPages, initialPageId })
  );

  const activePage = getPageByIdSelector({ sortedPages, pageId });
  const activePageIndex = getActivePageIndexSelector({ sortedPages, pageId });

  /**
   * Track the previous page ID so we can fire page.blur and page.focus events
   * when the active page changes. This ref is updated at the end of the useEffect.
   */
  const prevPageIdRef = useRef(null);

  useEffect(() => {
    const prevPageId = prevPageIdRef.current;

    /**
     * Fire page.blur on the previous page if it exists and the page actually changed.
     */
    if (prevPageId && prevPageId !== pageId) {
      const prevPage = getPageByIdSelector({ sortedPages, pageId: prevPageId });
      if (prevPage && onPageBlur) {
        onPageBlur(id, prevPage);
      }
    }

    /**
     * Fire page.focus on the new page. On initial mount prevPageId is null
     * so this fires for the first page load. On subsequent changes it fires
     * only when the page actually changed.
     */
    if (pageId && prevPageId !== pageId) {
      const newPage = getPageByIdSelector({ sortedPages, pageId });
      if (newPage && onPageFocus) {
        onPageFocus(id, newPage);
      }
    }

    prevPageIdRef.current = pageId;
  }, [pageId]);
  
   /* >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START NAVIGATION HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  // Navigation state for programmatic navigation
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const formPageScrollRef = useRef(null);      // For e-forms (FormPage internal scroll)
  const middlePanelBodyRef = useRef(null);     // For PDF forms (parent scroll)

  // Get the appropriate scroll container based on page type
  const getScrollContainer = useCallback((isPDF) => {

    return isPDF ? middlePanelBodyRef.current : formPageScrollRef.current;

  }, []);

  // Navigation handler for navigator.goto()
  const handleGoto = useCallback((path) => {

    const { pageId: targetPageId, fieldPositionId } = parseNavigationPath(path);

    if (!targetPageId) return { status: 'failure' };

    // Check if page exists in sortedPages (already filtered for visibility)
    const targetPage = sortedPages.find(p => p._id === targetPageId);
    if (!targetPage) return { status: 'failure' };

    let scrollPosition = 0;
    let status = 'success';
    const isPDF = !!targetPage.backgroundImage;

    // If fieldPositionId provided, validate and calculate scroll
    if (fieldPositionId) {

      /**
       * Use view-built page.fields when present (same merged list as activePage.fields,
       * with hidden from doc.fields). Otherwise merge fieldPositions with fields so we
       * have hidden before applyLogic, and hidden/non-visible targets get status 'failure'.
       */
      const pageFieldsWithHidden = (targetPage.fields && targetPage.fields.length > 0)
        ? targetPage.fields
        : (targetPage.fieldPositions || []).map((fp) => {
          const field = fields.find((f) => f._id === fp.field) || {};
          return { ...field, ...fp };
        });

      const visibleFields = applyLogicForPageFields({
        pageFields: pageFieldsWithHidden,
        fields,
        mode,
      });

      const targetField = visibleFields.find(f => f._id === fieldPositionId);

      if (!targetField) {
        // Field doesn't exist or is hidden: still navigate to page, scroll to top, return failure
        status = 'failure';
      } else {
        // Calculate scroll position
        const padding = targetPage.padding || 0;
        const rowHeight = targetPage.rowHeight || 8;

        if (isPDF) {

          // For PDF pages, calculate scaled position
          // PDF pages are scaled to fit the container, so we need to use scaled coordinates
          const pageWidth = targetPage.width || 816;
          const pageHeight = targetPage.height || 1056;
          const containerW = getContainerWidth();
          const containerH = getPageHeight();

          // Calculate scale factor (same logic as PDFPage.js)
          const pageMargin = targetPage.margin || 0;
          const pageBorderWidth = targetPage.borderWidth || 0;
          const adjustedContainerW = containerW - (padding * 2) - (pageMargin * 2) - (pageBorderWidth * 2);
          const adjustedContainerH = containerH - (padding * 2) - (pageMargin * 2) - (pageBorderWidth * 2);

          const percentageDiff = calculatePagePercentageAdjustments(pageHeight, pageWidth, adjustedContainerH, adjustedContainerW);

          // Calculate pixel position, then scale it
          const pixelY = targetField.y * rowHeight;
          const scaledPixelY = getPercentageAdjustedValue(pixelY, percentageDiff);

          scrollPosition = scaledPixelY;

        } else {
          // For FormPage, use unscaled coordinates
          scrollPosition = padding + (targetField.y * rowHeight);
        }
      }

    }

    // Navigate to page if different
    if (targetPageId !== pageId) {

      setPageId(targetPageId);

      setPendingNavigation({ scrollPosition, isPDF });

      setPageLoading(true);

      setTimeout(() => setPageLoading(false), 100);

    } else {

      // Same page - scroll with lazy loading support
      const scrollContainer = getScrollContainer(isPDF);

      if (scrollContainer) {

        // First scroll to trigger lazy loading
        scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        scrollContainer.dispatchEvent(new Event('scroll'));

        // Second scroll after lazy loading renders more content
        setTimeout(() => {
          scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
          scrollContainer.dispatchEvent(new Event('scroll'));
        }, 150);

      }

    }

    return { status };
  }, [sortedPages, fields, mode, pageId, getScrollContainer]);

  // Register/unregister with navigator
  useEffect(() => {

    if (navigator) {

      navigator._register(handleGoto);

      return () => navigator._unregister();

    }

  }, [navigator, handleGoto]);

  // Apply pending scroll after page load completes
  useEffect(() => {

    if (pendingNavigation && !pageLoading) {
      // Helper to apply scroll and trigger lazy loading update
      const applyScroll = () => {

        const scrollContainer = getScrollContainer(pendingNavigation.isPDF);

        if (scrollContainer) {

          scrollContainer.scrollTo({ top: pendingNavigation.scrollPosition, behavior: 'smooth' });

          // Dispatch scroll event to trigger lazy loading visibility check
          scrollContainer.dispatchEvent(new Event('scroll'));

        }

      };

      // With lazy loading, we need multiple scroll attempts:
      // 1. First scroll triggers lazy loading to start rendering fields
      // 2. Wait for lazy loading to render more content
      // 3. Second scroll ensures we reach the final position
      const timeoutId = setTimeout(() => {
        applyScroll();
        // Second attempt after lazy loading has time to render
        setTimeout(() => {
          applyScroll();
          setPendingNavigation(null);
        }, 150);
      }, 250);

      return () => clearTimeout(timeoutId);
    }

  }, [pageLoading, pendingNavigation, getScrollContainer]);

  // Callback to receive scroll container from FormPage
  const handleScrollContainerReady = useCallback((el) => {
    formPageScrollRef.current = el;
  }, []);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END NAVIGATION HELPERS <<<<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * Why do we force a page rerender when these properties change?
   *
   * 1. We have to force react-grid-layout to rerender when layouts,
   *    cols or rowHeights are changed so that the grid works properly.
   * 2. We have to force a rerender so that PDF Forms will generate
   *    a new percentage difference and resize the background image and
   *    field appropriatly based on the new width/height properties.
   */
  useEffect(() => {
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);
  }, [
    view,
    width,
    height,
    activePage?.layout,
    activePage?.cols,
    activePage?.rowHeight,
  ]);

  /**
   * This specifies what width to use for parent element that contains
   * the form, page navigation, and everything else within the middle
   * panel for all the different states of the SDK.
   */
  const getContainerWidth = () => {
    /**
     * Edit mode will use the width property for the primary view but
     * when mobile view is enabled it will use the default mobileWidth;
     */
    if (mode === FieldModes.edit) return view === FileViews.mobile ? 460 : width;
    else return width;
  };

  /**
   * Get the height of the page based on the specified element height paseed 
   * via parent component and subtraction of height needed for the page nav bar,
   * header, and footer.
   */
  const getPageHeight = () => {

    /**
     * Why +4? This is to account for small amount of white space padding between the header/footer
     * and the page itself.
     */
    const headerHeight = header && typeof header.height === 'number' ? (header?.height + 6) : 0;
    const footerHeight = footer && typeof footer.height === 'number' ? (footer?.height + 6) : 0;

    /**
     * Why -62?
     *
     * 62px represents the required height used by the page nav bar element (plus page nav
     * scrollbar height) itself, the margin between the page nav bar and the form page above,
     * and the border widths used by the form page above.
     */
    const pageNavAndPaddingHeight = 62;

    return height - (pageNavAndPaddingHeight + headerHeight + footerHeight);

  };

  /**
   * How to handle active page:
   *
   * There are two methods to have a field become the currently selected active field:
   * - Method 1: Page Id is externally changed and passed to SDK via initialPageId
   * - Method 2: User manually selects active page
   */

  /**
   * Method 1: Enable active page to be controlled via initialPageId prop. This allows
   * a user of the component to pass in a initialPageId and set it as active
   * and overwrite the current internal state;
   */
  useEffect(() => {
    if (initialPageId) {
      setPageId(initialPageId);
      setActivePageSettingsPageId(null);
    }
  }, [initialPageId]);


  /**
   * Method 2: User manually selects active page
   */
  const handlePageSelect = (nextActivePageId) => {
    /**
     * If page is currently active and page settings are already displayed for active page then do nothing.
     */
    if (
      nextActivePageId === pageId &&
      activePageSettingsPageId === nextActivePageId
    ) return;

    /**
     * Remove active field states from previous page.
     */
    setActiveFieldId(null);
    setActiveFieldSection(null);
    setActivePageSettingsPageId(null);
    setActiveTableColumnSettingsColumnId(null);
    setPageId(nextActivePageId);
    setActiveTableSettingsId(null);
    setActiveSchemaId(null);

    /**
     * Important pageLoading Description
     *
     * Why do we set a loading state for the page and then quickly set it to not be in a loading state?
     *
     * This is to force the GridLayout to be unmounted and remounted fresh. We are required
     * to set internal field state inside the GridLayout and this flushes all state and
     * UI to load the newly selected page with no internal state conflicts.
     */
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);
  };

  const handlePageCreate = useCallback(() => {
    if (onPageCreate) onPageCreate(id);
  }, [id]);

  const handlePageDuplicate = useCallback(
    (pageId) => {
      if (onPageDuplicate) onPageDuplicate(id, pageId);
    },
    [id]
  );

  const handlePageDelete = useCallback(
    (pageId) => {
      const targetDeletePageIndex = sortedPages.findIndex(
        (page) => pageId === page._id
      );

      const nextActivePageIndex =
        targetDeletePageIndex === 0
          ? targetDeletePageIndex + 1
          : targetDeletePageIndex - 1;
      const nextActivePageId = sortedPages[nextActivePageIndex]._id;

      /**
       * Fire page.blur eagerly for the deleted page before it is removed
       * from sortedPages. After onPageDelete runs, the page object will
       * no longer be available for the useEffect to look up.
       */
      if (onPageBlur) {
        const deletedPage = sortedPages[targetDeletePageIndex];
        if (deletedPage) onPageBlur(id, deletedPage);
      }

      /**
       * Set prevPageIdRef to null so the useEffect skips the duplicate
       * blur (null && ... is false) but still fires page.focus for the
       * new page (pageId && null !== pageId is true).
       */
      prevPageIdRef.current = null;

      setPageId(nextActivePageId);

      if (onPageDelete) onPageDelete(id, pageId);
    },
    [id, pageId, sortedPages, onPageBlur]
  );

  const handlePageMoveLeft = useCallback(
    (pageId) => {
      if (onPageMoveLeft) onPageMoveLeft(id, pageId);
    },
    [id, pageId]
  );

  const handlePageMoveRight = useCallback(
    (pageId) => {
      if (onPageMoveLeft) onPageMoveRight(id, pageId);
    },
    [id, pageId]
  );

  const handlePageChange = useCallback(
    (pageId, changes) => {
      if (onPageChange) onPageChange(id, pageId, changes);
    },
    [id, pageId, activePage?.fields]
  );

  const handlePageUpload = useCallback(
    (pageId, file) => {
      if (onPageUpload) onPageUpload(id, pageId, file);
    },
    [id]
  );

  const handlePageUploadDelete = useCallback(
    (pageId) => {
      if (onPageUploadDelete) onPageUploadDelete(id, pageId);
    },
    [id]
  );

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END PAGE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START FIELD HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */
  const [activeFieldId, setActiveFieldId] = useState();

  //Field section represents either the pageId, header or footer. This informs
  //us where the actual active field is located.
  const [activeFieldSection, setActiveFieldSection] = useState();

  const activePageFields = activePage
    ? applyLogicForPageFields({ pageFields: activePage.fields, fields, mode })
    : [];

  const activeField = getFieldByIdSelector({ page: activePage, header, footer, fieldId: activeFieldId })

  const handleSetActiveFieldId = (sectionId, fieldId) => {

    if (activeFieldId === fieldId) return;

    setActiveSchemaId(null);
    setActiveTableColumnSettingsColumnId(null);
    setActivePageSettingsPageId(null);
    setActiveFieldId(fieldId);
    setActiveFieldSection(sectionId);
  };

  /**
   * Handle CMD/CTRL + D to duplicate field
   */
  useEffect(() => {

    const handleKeyDown = (event) => {

      // Check for Ctrl + D or Cmd + D
      if ((event.ctrlKey || event.metaKey)
        && event.key.toLowerCase() === 'd'
        && activeField
        && mode === FieldModes.edit) {
        event.preventDefault();


        const multiFieldPositionFieldType = activeField.type === FieldTypes.multiSelect
          || activeField.type === FieldTypes.dropdown
          || activeField.type === FieldTypes.table;

        const multiFieldPositionDisplayType = activeField.displayType === FieldDisplayTypes.check
          || activeField.displayType === FieldDisplayTypes.circle
          || activeField.displayType === FieldDisplayTypes.square
          || activeField.displayType === FieldDisplayTypes.text
          || activeField.displayType === FieldDisplayTypes.radio;


        /**
         * Duplicate Scenario 1:
         * 
         * Duplicates ONLY the field position. This represents  the creation of a new
         * field position for an existing field. This is used when a displayType that 
         * represents a multi field position scenario exists like check, radio, etc. is 
         * present. In this case we want to keep the new field position connected to the 
         * original field.
         */
        if (multiFieldPositionFieldType && multiFieldPositionDisplayType) {
          handleFieldPositionDuplicate(activeField.field, activeField._id);
        }

        /**
        * Duplicate Scenario 2:
        * 
        * Duplicates the entire field and field position. This represents athe creation 
        * of a brand new field and field position within the section.
        */
        else {
          handleFieldDuplicate(activeField.field, activeField._id);
        }
      }
    };

    // Attach the event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeField]);

  //Set field as active when user manually selects active field
  const handleFieldSelect = useCallback(
    (
      sectionId, //Could be the actual page._id, header, or footer
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      rowId,
      columnId,
      columnIdentifier
    ) => {

      handleSetActiveFieldId(sectionId, fieldPositionId);

      if (mode === FieldModes.edit && onSelect) {
        onSelect(
          id,
          sectionId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          rowId,
          columnId,
          columnIdentifier
        );
      }
    },
    [pageId]
  );

  /**
   * What activeTableColumnSettingsColumnId is used for:
   *
   * activeTableFieldColumnSettingsColumnId is used to determine when and what
   * column settings to display in the settings panel.
   */
  const [activeTableColumnSettingsColumnId, setActiveTableColumnSettingsColumnId] = useState(null);
  const [activeTableSettingsId, setActiveTableSettingsId] = useState(null);

  /**
   * What activeSchemaId is used for:
   *
   * activeSchemaId is used to determine when and what
   * schema table settings to display in the settings panel.
   */
  const [activeSchemaId, setActiveSchemaId] = useState(null);

  /**
   * Check if a dragged field from a Tree is allowed to be duplicated onto
   * a different view. In this case it just checks if the active view already
   * has this field on it.
   *
   * Field syncing enabled or disabled does not matter as views are not allowed
   * to have duplicate fieldPositions when using the tree to drag existing
   * field positions onto a view
   */
  const fieldCreateValidityCheck = useCallback(
    (fieldPosition) => {
      if (!fieldPosition) return { valid: true, reason: '' };

      // Find matching view or use fallback pages
      const viewPages = views?.find((v) => v.type === view)?.pages || pages;

      const fields = viewPages?.reduce((acc, pg) => {
        return [...acc, ...(pg.fieldPositions || [])];
      }, []) || [];

      // Check if fieldPosition exists on the view receiving it
      const duplicate = fields.some((f) => {
        if (view === FileViews.mobile) return f.field === fieldPosition.field;
        else {
          return (
            fieldPosition.dragSource === FieldDragSourceTypes.tree &&
            f._id === fieldPosition._id
          );
        }
      });

      return {
        valid: !duplicate,
        reason: duplicate ? 'Field already exists on page or view.' : '',
      };
    },
    [views, pages, view]
  );

  /**
   * This creation handler represents the creation of a new
   * field position on the form.
   *
   * The fieldPosition._id that gets added to the object is meant to be used as the
   * fieldPosition._id and not the field._id.
   *
   * What is the difference between fieldPosition and field? I'm glad you asked!
   *
   * fieldPosition
   * - Represents the style and placement coordinates on the form.
   * - There could be multiple fieldPositions for a single field if you're supporting multiple form views.
   *
   * field
   * - Represents the actual field data portion. This is the value, configs, etc.
   */
  const handleFieldCreate = useCallback(
    (targetPageId, fieldPosition) => {

      // Check if a tree created field position is created within the proper view
      if (fieldPosition._id) {
        const { valid, reason } = fieldCreateValidityCheck(fieldPosition);
        if (!valid) {
          /* eslint-disable no-alert */
          alert(reason);
          return;
        }
      }

      const newId = generateObjectId();
      const newFieldPosition = { ...fieldPosition, _id: newId };

      /**
       * We don't want to save a placeholder description at this time with the field.
       * In the future a description value will be used to display explainer
       * text below the field.
       */
      if (newFieldPosition.description) delete newFieldPosition.description;

      const fieldId = generateId(useReadableIds, {
        fields,
        pages,
        views
      }, newFieldPosition.type);

      if (!newFieldPosition.field) newFieldPosition.field = fieldId;
      if (!newFieldPosition.identifier) newFieldPosition.identifier = `field_${fieldId}`;

      if (!newFieldPosition.displayType) newFieldPosition.displayType = FieldDisplayTypes.original;

      if (fieldPosition && fieldPosition.tableColumns && Array.isArray(fieldPosition.tableColumns)) {

        newFieldPosition.tableColumns = ensureTableColumnsHaveRequiredProperties(
          fieldPosition.tableColumns,
          newFieldPosition,
          fieldId,
          useReadableIds
        );

        if (!newFieldPosition.tableColumnOrder) {
          newFieldPosition.tableColumnOrder = getColumnOrder(null, newFieldPosition.tableColumns);
        }

      }

      if (fieldPosition && fieldPosition.schema && fieldPosition.type === FieldTypes.collection) {

        newFieldPosition.schema = ensureCollectionSchemaTableColumnsHaveRequiredProperties(
          fieldPosition.schema,
          newFieldPosition,
          fieldId,
          useReadableIds
        );

      }

      if (fieldPosition.type === FieldTypes.table) {
        //Ensure rowOrder is properly added to newly created fields
        if (fieldPosition.value && !fieldPosition.rowOrder) newFieldPosition.rowOrder = getRowOrder(null, fieldPosition.value);
      }

      /**
       * Logic below hides field title for newly added fields  by default if it's a pdf form
       */
      if (
        activePage &&
        activePage.presentation === FieldPresentationModes.transparent
      ) {
        newFieldPosition.titleDisplay = 'none';
      }

      if (newFieldPosition.dragSource) delete newFieldPosition.dragSource;

      if (onFieldCreate) onFieldCreate(id, targetPageId, newFieldPosition);

      // Automatically select the newly created field so the settings panel switches to it
      if (mode === FieldModes.edit) {
        handleFieldSelect(
          targetPageId,
          newFieldPosition.field,
          newFieldPosition.identifier,
          newFieldPosition._id,
          newFieldPosition.rowId,
          newFieldPosition.column || newFieldPosition.columnId,
          newFieldPosition.columnIdentifier
        );
      }

    },
    [
      id,
      pageId,
      activePage,
      activeFieldId,
      fields,
      isMobileView,
      mode,
      handleFieldSelect,
    ]
  );

  const handleFieldDuplicate = useCallback(
    (fieldId, fieldPositionId) => {
      if (onFieldDuplicate) onFieldDuplicate(activeFieldSection, fieldId, fieldPositionId);
    },
    [pageId, activeFieldSection]
  );

  const handleFieldPositionDuplicate = useCallback((fieldId, fieldPositionId) => {
    if (onFieldPositionDuplicate) onFieldPositionDuplicate(pageId, fieldId, fieldPositionId);
  }, [pageId]);

  const handleFieldChange = useCallback((fieldId, fieldIdentifier, fieldPositionId, data, collectionFieldParams, eventType, localTimezone) => {

    if (onFieldChange) onFieldChange(id, activeFieldSection, fieldId, fieldIdentifier, fieldPositionId, data, collectionFieldParams, eventType, localTimezone);

  }, [id, activeFieldSection]);

  const handleFieldUpload = useCallback(
    (
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      files,
      loadingCallback,
      errorCallback
    ) => {
      if (onFieldUpload) {
        onFieldUpload(
          id,
          pageId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          files,
          loadingCallback,
          errorCallback
        );
      }
    },
    [id, pageId]
  );

  const handleFieldPositionChange = useCallback((fieldId, fieldIdentifier, fieldPositionId, data) => {
    if (onFieldPositionChange) onFieldPositionChange(id, activeFieldSection, fieldId, fieldIdentifier, fieldPositionId, data);
  }, [id, activeFieldSection]);

  const handleFieldDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId) => {
    if (onFieldDelete) onFieldDelete(id, activeFieldSection, fieldId, fieldIdentifier, fieldPositionId);
  }, [id, activeFieldSection]);

  const handleLayoutChange = useCallback((sectionId, positions) => {
    if (onFieldLayoutsChange) onFieldLayoutsChange(id, sectionId, positions);
  }, [id]);

  const handleFieldFileClick = useCallback((fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.
    if (onFieldFileClick) onFieldFileClick(id, pageId, fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams);
  }, [id, pageId]);

  const handleFieldFileDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.
    if (onFieldFileDelete) onFieldFileDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId, fileData, fileIndex, extraParams);
  }, [id, pageId]);

  const handleFieldFileUpload = useCallback((fieldId, fieldIdentifier, fieldPositionId, files, loadingCallback, errorCallback) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.
    if (onFieldFileUpload) onFieldFileUpload(id, pageId, fieldId, fieldIdentifier, fieldPositionId, files, loadingCallback, errorCallback);
  }, [id, pageId]);

  const handleFieldRowCellChange = useCallback(
    (fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, timezone) => {
      //We don't use sectionId here because this change only targets the field and not field position. Only changes
      //that impact field position should use section ids.
      if (onFieldRowCellChange) {
        onFieldRowCellChange(
          id,
          pageId,
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          rowId,
          columnId,
          change,
          timezone
        );
      }
    },
    [id, pageId]
  );

  const handleFieldRowCellUpload = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, loadingCallback, errorCallback) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.
    if (onFieldRowCellUpload) onFieldRowCellUpload(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, change, loadingCallback, errorCallback);
  }, [id, pageId]);

  const handleFieldRowAddBelow = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.
    if (onFieldRowAddBelow) onFieldRowAddBelow(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);
  }, [id, pageId]);

  const handleFieldRowDuplicate = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.

    if (onFieldRowDuplicate) onFieldRowDuplicate(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowMoveUp = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.

    if (onFieldRowMoveUp) onFieldRowMoveUp(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowMoveDown = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.

    if (onFieldRowMoveDown) onFieldRowMoveDown(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldRowDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId, rowIndex) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.

    if (onFieldRowDelete) onFieldRowDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }, [id, pageId]);

  const handleFieldColumnDelete = useCallback((fieldId, fieldIdentifier, fieldPositionId, columnId) => {
    //We don't use sectionId here because this change only targets the field and not field position. Only changes
    //that impact field position should use section ids.

    setActiveTableColumnSettingsColumnId(null);

    if (onFieldColumnDelete) onFieldColumnDelete(id, pageId, fieldId, fieldIdentifier, fieldPositionId, columnId);

  }, [id, pageId]);

  const handleFocus = useCallback((fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath) => {

    if (onFocus) onFocus(id, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath);

  }, [id, pageId]);

  const handleBlur = useCallback((fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath) => {

    if (onBlur) onBlur(id, pageId, fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier, parentPath);

  }, [id, pageId]);

  const handleMeasurementsCompleted = useCallback((sectionId, fieldId, fieldIdentifier, fieldPositionId, rect) => {
    if (onMeasurementsCompleted) onMeasurementsCompleted(id, sectionId, fieldId, fieldIdentifier, fieldPositionId, rect);
  }, [id]);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END FIELD HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> START FILE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  /**
   * This function is used to reset the entire, file, page and field states. This
   * is mainly used for when a new doc is loaded into the SDK and we want to
   * reset all internal states to the initial values.
   */
  const handleFileAndPageReset = () => {
    setPageId(getActivePageIdSelector({ sortedPages, initialPageId }));
    setActivePageSettingsPageId(null);
    setActiveFieldId(null);
    setActiveFieldSection(null);
    setActiveTableColumnSettingsColumnId(null);
    setActiveSchemaId(null);

    /**
     * Important pageLoading Description
     *
     * Why do we set a loading state for the page and then quickly set it to not be in a loading state?
     *
     * This is to force the GridLayout to be unmounted and remounted fresh. We are required
     * to set internal field state inside the GridLayout and this flushes all state and
     * UI to load the newly selected page with no internal state conflicts.
     */
    setPageLoading(true);
    setTimeout(() => setPageLoading(false), 100);
  }

  /**
   * Reset Required Scenario #1: State when file is changed and that is demonstrated by the id prop changing.
   */
  useEffect(handleFileAndPageReset, [id]);

  /**
   * Reset Required Scenario #2: State when file id has not changed compared to what is in state
   * but the file is actually different. This is demonstrated by having different pages. 
   */
  useEffect(() => {
    if (!activePage && pageId && sortedPages && sortedPages.length > 0) handleFileAndPageReset();
  }, [activePage]);

  /**
   * VERY IMPORTANT NOTE on handleViewChange:
   * 
   * This function is used to handle view changes. It is used to force a page
   * reset and remount the GridLayout to load the new view with no internal state conflicts or references
   * to the previous view field measurements. 
   * 
   * This is to ensure that the GridLayout is always loaded with the latest field measurements and
   * state for the new view. Without this, the GridLayout will continue to reference the previous view
   * field measurements and state, which will cause the GridLayout to not load the new view correctly 
   * and desktop measures to be used in mobile view and vice versa.
   */
  const handleViewChange = useCallback((nextView) => {
    /**
     * Important pageLoading Description
     *
     * Why do we set a loading state for the page and then quickly set it to not be in a loading state?
     *
     * This is to force the GridLayout to be unmounted and remounted fresh. We are required
     * to set internal field state inside the GridLayout and this flushes all state and
     * UI to load the newly selected page with no internal state conflicts.
     */
    setPageLoading(true);
    if (onViewChange) onViewChange(nextView);
    setTimeout(() => setPageLoading(false), 100);
  }, []);

  /**
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   * >>>>> END FILE HELPERS <<<<<
   * >>>>>>>>>>>>>>>>>>>>>>>>>>>>>
   */

  // Formula testing state
  if (!activePage || !pageId) {
    return (
      <StyledLoadingWrapper>
        <LoadingIndicator size="lg" />
      </StyledLoadingWrapper>
    );
  }


  const tableEditorSettingsNode = mode === FieldModes.edit && (activeField && (activeField.type === FieldTypes.table || activeField.type === FieldTypes.inputGroup)) ? (
    <StyledMiddlePanelTableSettings>
      <TableEditorSettings
        display
        id={activeField ? activeField._id : 'pending-create'}
        activeFieldPosition={activeField}
        onChange={handleFieldChange}
        onColumnEdit={(fieldId, columnId) => setActiveTableColumnSettingsColumnId(columnId)}
        view={view}
        disableDragAndDrop={view === FileViews.mobile || activeField?.type === FieldTypes.inputGroup}
        features={features}
        fieldSettings={fieldSettings}
      />
    </StyledMiddlePanelTableSettings>
  ) : null;

  const collectionEditorSettingsNode = mode === FieldModes.edit && (activeField && (activeField.type === FieldTypes.collection)) ? (
    <StyledMiddlePanelTableSettings>
      <CollectionEditorSettings
        display
        id={activeField ? activeField._id : 'pending-create'}
        activeFieldPosition={activeField}
        onChange={handleFieldChange}
        onColumnEdit={(fieldId, columnId, tableId) => {
          setActiveSchemaId(null);
          setActiveTableColumnSettingsColumnId(columnId);
          setActiveTableSettingsId(tableId);
        }}
        onSchemaEdit={(schemaId) => {
          setActiveTableColumnSettingsColumnId(null);
          setActiveTableSettingsId(null);
          setActiveSchemaId(schemaId);
        }}
        view={view}
        disableDragAndDrop={view === FileViews.mobile || activeField?.type === FieldTypes.inputGroup}
        features={features}
        fieldSettings={fieldSettings}
      />
    </StyledMiddlePanelTableSettings>
  ) : null;

  const tableColumnSettingsNode = !activeTableColumnSettingsColumnId || !activeField ||
    (activeField.type !== FieldTypes.table && activeField.type !== FieldTypes.inputGroup && activeField.type !== FieldTypes.collection)
    ? null : (
      <TableColumnSettings
        {...activeField}
        config={fieldSettings}
        activeFieldPosition={activeField}
        columnId={activeTableColumnSettingsColumnId}
        tableId={activeTableSettingsId}
        onPositionChange={handleFieldPositionChange}
        onFieldColumnDelete={handleFieldColumnDelete}
        onColumnIdChange={(newColumnId) => setActiveTableColumnSettingsColumnId(newColumnId)}
        onCapture={onCapture}
        onChange={(fieldId, fieldIdentifier, fieldPositionId, data, columnData) => {
          // Pass columndata only when column ID changes (for updating fieldPositions)
          if (columnData && columnData.oldColumnId !== columnData.newColumnId) {
            handleFieldChange(fieldId, fieldIdentifier, fieldPositionId, data, columnData);
          } else {
            handleFieldChange(fieldId, fieldIdentifier, fieldPositionId, data);
          }
        }}
        fields={fields}
        useReadableIds={useReadableIds}
      />
    );

  const pageNavNode = (
    <StyledMiddlePanelPageBarWrapper theme={theme} $isMobileView={isMobileView}>
      <PageList
        view={view}
        mode={mode}
        config={fieldSettings?.page}
        sortedPages={sortedPages}
        activePageId={pageId}
        onSelect={handlePageSelect}
        onCreate={handlePageCreate}
        onViewChange={handleViewChange}
        onSettingsClick={setActivePageSettingsPageId}
        onFileSettingsClick={() => {
          setActivePageSettingsPageId(null);
          handleSetActiveFieldId(null, null);
        }}
        onDuplicate={handlePageDuplicate}
        onDelete={handlePageDelete}
        onMoveLeft={handlePageMoveLeft}
        onMoveRight={handlePageMoveRight}
      />
    </StyledMiddlePanelPageBarWrapper>
  );

  /**
   * These are the props that are passed to the header, footer, and page
   */
  const defaultPageProps = {

    mode: modeOverride,
    view,

    focused: activeFieldId,
    fieldDefaultStyles: styles,

    onFieldChange: handleFieldChange,
    onFieldSelect: handleFieldSelect,
    onFieldCreate: handleFieldCreate,
    onFieldUpload: handleFieldUpload,
    onFieldLayoutsChange: handleLayoutChange,
    onFieldColumnDelete: handleFieldColumnDelete,
    onTableFieldCellUpdate: handleFieldRowCellChange,
    onTableFieldCellUpload: handleFieldRowCellUpload,
    onTableFieldRowAddBelow: handleFieldRowAddBelow,
    onTableFieldRowDuplicate: handleFieldRowDuplicate,
    onTableFieldRowMoveUp: handleFieldRowMoveUp,
    onTableFieldRowMoveDown: handleFieldRowMoveDown,
    onTableFieldRowDelete: handleFieldRowDelete,
    onFocus: handleFocus,
    onBlur: handleBlur,

    onFieldFileUpload: handleFieldFileUpload,
    onFieldFileClick: handleFieldFileClick,
    onFieldFileDelete: handleFieldFileDelete,

    measure,
    onMeasurementsCompleted: handleMeasurementsCompleted,

    onCapture,

    enableLazyLoadFields,
    mockTestDndMonitor,
    onUploadAsync,
    documentId,
    documentIdentifier,
    fileId: id,
    fieldSettings: fieldSettings,
    useReadableIds,
    licenseValidation,

    config: fieldSettings

  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledWrapper theme={theme} data-testid="file">
          {mode !== FieldModes.edit || isMobileView ? null : (
            <StyledLeftPanelWrapper theme={theme}>
              <BuilderTabs
                fieldOptions={fieldOptions}
                views={views}
                view={view}
                filePages={pages}
                filePageOrder={pageOrder}
                fileId={id}
                fields={fields}
                mode={mode}
                height={height}
                presentation={activePage.presentation}
                activeFieldId={activeFieldId}
                activeField={activeField}
                onFieldSelect={handleFieldSelect}
                activePageId={pageId}
                onPageSelect={handlePageSelect}
                onViewSelect={handleViewChange}
                hasMobileView={hasMobileView}
              />
            </StyledLeftPanelWrapper>
          )}
          <StyledMiddlePanel
            style={{
              /**
               * Right now this is used to ensure that the page navigation bar is the same
               * width as the middle panel form. Without this the page navigation bar will
               * extend pass the desired width and cause horizontal scrolling issues.
               */
              width: getContainerWidth(),
              maxWidth: getContainerWidth(),
              height,
              maxHeight: height,
            }}
          >
            {isMobileView ? pageNavNode : null}
            {!header || view === FileViews.mobile ? null : (
              <>
                <StyledMiddlePanelBody theme={theme} $maxHeight={header?.height || 0}>
                  {pageLoading ? (
                    <StyledMiddlePanelLoader />
                  ) : (
                    <Page
                      {...{
                        containerHeight: header?.height || 0,
                        containerWidth: getContainerWidth(),
                        ...defaultPageProps,
                        ...header,
                        measurements: measurements['header'],
                        updateTimestamps: updateTimestamps['header'],
                        id: 'header',
                      }}
                    />
                  )}
                </StyledMiddlePanelBody>
                <b style={{ padding: '2px' }}></b>
              </>
            )}
            <StyledMiddlePanelBody theme={theme} ref={middlePanelBodyRef}>
              {pageLoading ? (
                <StyledMiddlePanelLoader />
              ) : (
                <Page
                  {...{
                    containerHeight: getPageHeight(),
                    containerWidth: getContainerWidth(),
                    ...defaultPageProps,
                    ...activePage,
                    id: activePage._id,
                    fields: activePageFields,
                    measurements: measurements[activePage._id],
                    updateTimestamps: updateTimestamps[activePage._id],
                    onScrollContainerReady: handleScrollContainerReady,
                  }}
                />
              )}
            </StyledMiddlePanelBody>
            {!footer || view === FileViews.mobile ? null : (
              <>
                <b style={{ padding: '2px' }}></b>
                <StyledMiddlePanelBody theme={theme} $maxHeight={footer?.height || 0}>
                  {pageLoading ? (
                    <StyledMiddlePanelLoader />
                  ) : (
                    <Page
                      {...{
                        containerHeight: footer?.height || 0,
                        containerWidth: getContainerWidth(),
                        ...defaultPageProps,
                        ...footer,
                        measurements: measurements['footer'],
                        updateTimestamps: updateTimestamps['footer'],
                        id: 'footer',
                      }}
                    />
                  )}
                </StyledMiddlePanelBody>
              </>
            )}
            {tableEditorSettingsNode}
            {collectionEditorSettingsNode}
            {isMobileView ? null : pageNavNode}
          </StyledMiddlePanel>
          {mode !== FieldModes.edit || isMobileView ? null : (
            <StyledRightPanelWrapper theme={theme}>
              {tableColumnSettingsNode}
              {!activeField || activeTableColumnSettingsColumnId ? null : (
                <FieldSettings
                  fileId={id}
                  view={view}
                  mode={mode}
                  {...activeField}
                  activeSchemaId={activeSchemaId}
                  sortedPages={sortedPages}
                  fields={fields}
                  config={fieldSettings}
                  activeFieldPosition={activeField}
                  activeFieldSection={activeFieldSection}
                  onChange={handleFieldChange}
                  onUpload={handleFieldFileUpload}
                  onDelete={handleFieldDelete}
                  onDuplicate={handleFieldDuplicate}
                  onPositionChange={handleFieldPositionChange}
                  onPositionDuplicate={handleFieldPositionDuplicate}
                  fieldDefaultStyles={styles}
                  fieldMode={activePage.presentation}
                  onCapture={onCapture}
                  useReadableIds={useReadableIds}
                  onSchemaEdit={(schemaId) => {
                    setActiveSchemaId(schemaId);
                  }}
                />
              )}
              {!activePageSettingsPageId ? null : (
                <PageSettings
                  fileId={id}
                  view={view}
                  pageIndex={activePageIndex}
                  page={activePage}
                  sortedPages={sortedPages}
                  fields={fields}
                  config={fieldSettings?.page}
                  hidden={activePage?.hidden}
                  onChange={handlePageChange}
                  onDelete={pages.length < 2 ? null : handlePageDelete}
                  onUpload={handlePageUpload}
                  onUploadDelete={handlePageUploadDelete}
                />
              )}
              {!activeField && !activePageSettingsPageId ? (
                <FileSettings
                  id={id}
                  view={view}
                  name={name}
                  header={header}
                  footer={footer}
                  styles={styles}
                  config={fieldSettings?.file}
                  onChange={onFileChange}
                  onUpload={onFileUpload}
                  mode={mode}
                  features={features}
                  fieldMode={activePage.presentation}
                  views={views}
                  onToggleViewSync={onToggleViewSync}
                  hasMobileView={hasMobileView}
                  doc={doc}
                  onDocumentChange={onDocumentChange}
                />
              ) : null}
            </StyledRightPanelWrapper>
          )}
        </StyledWrapper>
      </DndProvider>
    </>
  );
};

File.propTypes = {
  mode: PropTypes.string,
  id: PropTypes.string, //File Id
  name: PropTypes.string,
  styles: PropTypes.object,
  defaultPageId: PropTypes.string,
  views: PropTypes.any,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      layout: PropTypes.string,
      presentation: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      cols: PropTypes.number,
      rowHeight: PropTypes.number,
      fields: PropTypes.array,
      padding: PropTypes.number,
      margin: PropTypes.number,
      borderWidth: PropTypes.number,
      borderColor: PropTypes.string,
      borderStyle: PropTypes.string,
      backgroundImage: PropTypes.string,
    })
  ).isRequired,
  pageOrder: PropTypes.array,
  onFileChange: PropTypes.func,
  onFileUpload: PropTypes.func,
  onDocumentChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onPageCreate: PropTypes.func,
  onPageUpload: PropTypes.func,
  onPageUploadDelete: PropTypes.func,
  onFieldCreate: PropTypes.func,
  onFieldChange: PropTypes.func,
  onFieldUpload: PropTypes.func,
  onFieldDelete: PropTypes.func,
  onFieldLayoutsChange: PropTypes.func,
  onViewChange: PropTypes.func,
  onToggleViewSync: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onPageFocus: PropTypes.func,
  onPageBlur: PropTypes.func,
  doc: PropTypes.object,
  features: PropTypes.object,
  useReadableIds: PropTypes.bool,
};

export const getFieldLookupSelector = createSelector(
  (state) => state.page,
  (state) => state.header,
  (state) => state.footer,
  (page, header, footer) => {
    const lookup = {};
    page?.fields.forEach((field) => (lookup[field._id] = field));
    header?.fields.forEach((field) => (lookup[field._id] = field));
    footer?.fields.forEach((field) => (lookup[field._id] = field));
    return lookup;
  }
);

export const getFieldByIdSelector = createSelector(
  getFieldLookupSelector,
  (state) => state.fieldId,
  (lookup, fieldId) => {
    return lookup[fieldId];
  }
);

const getActivePageIdSelector = createSelector(
  (state) => state.sortedPages,
  (state) => state.initialPageId,
  (sortedPages, initialPageId) => {
    if (!sortedPages || sortedPages.length < 1) return null;

    const pageIndex = !initialPageId
      ? -1
      : sortedPages.findIndex((page) => initialPageId === page._id);

    return pageIndex === -1 ? sortedPages[0]._id : sortedPages[pageIndex]._id;
  }
);

export const getPageLookupSelector = createSelector(
  (state) => state.sortedPages,
  (sortedPages) => {
    const lookup = {};
    sortedPages.forEach((page) => (lookup[page._id] = page));
    return lookup;
  }
);

export const getPageByIdSelector = createSelector(
  getPageLookupSelector,
  (state) => state.pageId,
  (lookup, pageId) => {
    return lookup[pageId];
  }
);

export const getActivePageIndexSelector = createSelector(
  (state) => state.sortedPages,
  (state) => state.pageId,
  (sortedPages, pageId) => {
    return sortedPages.findIndex((page) => page._id === pageId);
  }
);

export const getSortedPagesSelector = createSelector(
  (state) => state.pageOrder,
  (state) => state.pages,
  (pageOrder, pages) => {
    return sortPages(pages, pageOrder);
  }
);

export const applyLogicAndSortPagesSelector = createSelector(
  getSortedPagesSelector,
  (state) => state.fields,
  (state) => state.mode,
  (pages, fields, mode) => {
    if (mode === FieldModes.edit) return pages;
    return applyLogic(pages, fields, '_id').filter((item) => !item.hidden);
  }
);

export const applyLogicForPageFields = createSelector(
  (state) => state.pageFields,
  (state) => state.fields,
  (state) => state.mode,
  (pageFields, fields, mode) => {
    if (mode === FieldModes.edit) return pageFields;

    return applyLogic(pageFields, fields, '_id').filter((item) => !item.hidden);
  }
);

export default React.memo(File);
